import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, StyleSheet, TouchableOpacity, Vibration } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { Colors } from '../../theme';
import { useTheme } from '../../theme/useTheme';
import type { TonyteTheme } from '../../theme/themes';
import { daylightStyle } from '../../theme/daylightStyle';

interface Props {
  expanded: boolean;
  onPickPhoto: () => void;
  onTakePhoto: () => void;
  onShareLocation: () => void;
  onGif: () => void;
  onVoice: () => void;
}

function vibrate() {
  try {
    Vibration.vibrate(6);
  } catch {
    // best-effort only
  }
}

const ACTIONS: Array<{ key: string; icon: string; handlerKey: keyof Props }> = [
  { key: 'photo', icon: 'image', handlerKey: 'onPickPhoto' },
  { key: 'camera', icon: 'camera', handlerKey: 'onTakePhoto' },
  { key: 'location', icon: 'map-pin', handlerKey: 'onShareLocation' },
  { key: 'gif', icon: 'film', handlerKey: 'onGif' },
  { key: 'voice', icon: 'mic', handlerKey: 'onVoice' },
];

/** Expandable row of quick-action icons next to the input field. */
const QuickActionsRow: React.FC<Props> = (props) => {
  const { expanded } = props;
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const widthAnim = useRef(new Animated.Value(expanded ? 1 : 0)).current;

  useEffect(() => {
    Animated.spring(widthAnim, { toValue: expanded ? 1 : 0, useNativeDriver: false, speed: 18, bounciness: 4 }).start();
  }, [expanded, widthAnim]);

  return (
    <Animated.View
      style={[
        styles.row,
        {
          opacity: widthAnim,
          maxWidth: widthAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 220] }),
        },
      ]}
      pointerEvents={expanded ? 'auto' : 'none'}
    >
      {ACTIONS.map(action => (
        <TouchableOpacity
          key={action.key}
          style={[styles.actionBtn, appearance.actionBtn]}
          activeOpacity={0.8}
          onPress={() => {
            vibrate();
            (props[action.handlerKey] as () => void)();
          }}
        >
          <Icon name={action.icon} size={16} color={theme.isDark ? Colors.textPrimary : theme.colors.icon} />
        </TouchableOpacity>
      ))}
    </Animated.View>
  );
};

export default QuickActionsRow;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    overflow: 'hidden',
  },
  actionBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  actionBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    shadowColor: '#8A5A32',
    shadowOpacity: 0.12,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  }),
});
